import { Badge, Button, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  listMyAuthorizationsOptions,
  listMyAuthorizationsQueryKey,
  revokeMyAuthorizationMutation,
  type TokenAuthorizationResponse,
} from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section/QuerySection";
import { Text } from "@/components/Text";
import { isAuthorizationPurpose, needsReauth } from "@/features/account/authorizations";
import { formatDate } from "@/i18n/format";

function usePurposeLabel() {
  const { t } = useTranslation("settings");
  return (purpose: string) => (isAuthorizationPurpose(purpose) ? t(`authorizations.purposes.${purpose}`) : purpose);
}

function GrantRow({ grant, onRevoke }: Readonly<{ grant: TokenAuthorizationResponse; onRevoke: () => void }>) {
  const { t } = useTranslation("settings");
  const purposeLabel = usePurposeLabel();
  const targets = grant.syncTargets ?? [];
  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <Text as="span" variant="body">
              {purposeLabel(grant.purpose)}
            </Text>
            {needsReauth(grant) ? (
              <Badge variant="warning">{t("authorizations.status.reauth")}</Badge>
            ) : (
              <Badge variant="success">{t("authorizations.status.active")}</Badge>
            )}
          </span>
          <Text as="span" variant="body-sm" tone="tertiary">
            {t("authorizations.dates", {
              authorized: formatDate(grant.authorizedAt),
              lastUsed: formatDate(grant.lastUsedAt),
            })}
          </Text>
        </div>
        <Button variant="secondary" size="sm" onClick={onRevoke}>
          {t("authorizations.revoke")}
        </Button>
      </div>
      {targets.length > 0 && (
        <ul aria-label={t("authorizations.targets")} className="flex flex-col gap-1 pl-0 md:pl-4">
          {targets.map((target) => (
            <li key={target.uuid} className="flex flex-wrap items-center gap-x-2">
              <Text as="span" variant="body-sm">
                {target.name}
              </Text>
              <Text as="span" variant="caption" tone="tertiary">
                {target.lastSyncedAt
                  ? t("authorizations.lastSync", { date: formatDate(target.lastSyncedAt) })
                  : t("authorizations.neverSynced")}
              </Text>
              {target.reauthorizationRequired && (
                <Badge variant="warning" size="compact">
                  {t("authorizations.status.reauth")}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AuthorizationsPage() {
  const { t } = useTranslation("settings");
  const toast = useToast();
  const queryClient = useQueryClient();
  const purposeLabel = usePurposeLabel();
  const grants = useQuery(listMyAuthorizationsOptions());
  const [target, setTarget] = useState<TokenAuthorizationResponse | null>(null);
  const revoke = useMutation({
    ...revokeMyAuthorizationMutation(),
    onSuccess: async ({ data: revoked }) => {
      await queryClient.invalidateQueries({ queryKey: listMyAuthorizationsQueryKey() });
      if (revoked) toast.success(t("authorizations.revoked"));
      else toast.info(t("authorizations.alreadyRevoked"));
      setTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error, t("authorizations.revokeFailed"))),
  });
  const items = grants.data?.data ?? [];

  return (
    <Page title={t("authorizations.title")} subtitle={t("authorizations.subtitle")}>
      <QuerySection
        query={grants}
        isEmpty={() => items.length === 0}
        empty={{
          view: (
            <EmptyState
              icon={<KeyRound size={32} />}
              title={t("authorizations.empty")}
              body={t("authorizations.emptyBody")}
            />
          ),
        }}
      >
        {() => (
          <div className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">
            {items.map((grant) => (
              <GrantRow key={grant.uuid} grant={grant} onRevoke={() => setTarget(grant)} />
            ))}
          </div>
        )}
      </QuerySection>
      <ConfirmDialog
        open={target !== null}
        title={t("authorizations.revokeTitle")}
        body={t("authorizations.revokeBody", { label: target ? purposeLabel(target.purpose) : "" })}
        confirmLabel={t("authorizations.revoke")}
        loading={revoke.isPending}
        onConfirm={() => target && revoke.mutate({ path: { purpose: target.purpose } })}
        onClose={() => setTarget(null)}
      />
    </Page>
  );
}
